<script setup lang="ts">
import { ChevronLeft, ChevronRight, Download } from 'lucide-vue-next';
import type { PDFDocumentProxy, PDFPageProxy, PageViewport } from 'pdfjs-dist';

const props = withDefaults(defineProps<{
  source: Blob | string;
  title: string;
  backdropImageUrl?: string;
  loadingLabel?: string;
  errorLabel?: string;
}>(), {
  loadingLabel: 'Preparing document…',
  errorLabel: 'The proposal PDF could not be displayed.',
});

const emit = defineEmits<{ open: [] }>();
const stage = ref<HTMLElement | null>(null);
const loading = ref(true);
const loadError = ref(false);
const pageCount = ref(0);
const currentPage = ref(1);
const pageCanvases = new Map<number, HTMLCanvasElement>();
const pageSizes = ref<Record<number, { width: number; height: number }>>({});
const pageLinks = ref<Record<number, Array<{ href: string; left: number; top: number; width: number; height: number }>>>({});
let documentProxy: PDFDocumentProxy | undefined;
let resizeObserver: ResizeObserver | undefined;
let observedStageWidth = 0;
let renderQueue: Promise<void> = Promise.resolve();

function setPageCanvas(pageNumber: number, element: Element | ComponentPublicInstance | null) {
  if (element instanceof HTMLCanvasElement) pageCanvases.set(pageNumber, element);
  else pageCanvases.delete(pageNumber);
}

async function updatePageLinks(activeDocument: PDFDocumentProxy, pageNumber: number, page: PDFPageProxy, viewport: PageViewport) {
  let links: Array<{ href: string; left: number; top: number; width: number; height: number }> = [];
  try {
    const annotations = await page.getAnnotations({ intent: 'display' });
    if (Array.isArray(annotations)) {
      links = annotations.flatMap((annotation: any) => {
        try {
          if (annotation.subtype !== 'Link' || typeof annotation.url !== 'string' || !Array.isArray(annotation.rect)) return [];
          const url = new URL(annotation.url);
          if (!['http:', 'https:'].includes(url.protocol) || url.username || url.password) return [];
          const rect = [
            ...viewport.convertToViewportPoint(annotation.rect[0], annotation.rect[1]),
            ...viewport.convertToViewportPoint(annotation.rect[2], annotation.rect[3]),
          ];
          const left = Math.min(rect[0]!, rect[2]!);
          const top = Math.min(rect[1]!, rect[3]!);
          const width = Math.abs(rect[2]! - rect[0]!);
          const height = Math.abs(rect[3]! - rect[1]!);
          if (![left, top, width, height].every(Number.isFinite) || width <= 0 || height <= 0) return [];
          return [{ href: url.toString(), left: left / viewport.width * 100, top: top / viewport.height * 100, width: width / viewport.width * 100, height: height / viewport.height * 100 }];
        } catch { return []; }
      });
    }
  } catch {
    // Link extraction is optional and must not interfere with page rendering.
  }

  if (activeDocument === documentProxy) pageLinks.value = { ...pageLinks.value, [pageNumber]: links };
}

async function renderPages() {
  if (!documentProxy || !stage.value) return;
  const activeDocument = documentProxy;
  const availableWidth = Math.max(280, stage.value.clientWidth);
  for (let number = 1; number <= activeDocument.numPages; number += 1) {
    const page = await activeDocument.getPage(number);
    const baseViewport = page.getViewport({ scale: 1 });
    const viewport = page.getViewport({ scale: Math.min(availableWidth / baseViewport.width, 2) });
    pageSizes.value = { ...pageSizes.value, [number]: { width: viewport.width, height: viewport.height } };

    const canvas = pageCanvases.get(number);
    if (!canvas) continue;
    const outputScale = Math.min(window.devicePixelRatio || 1, 2);
    canvas.width = Math.floor(viewport.width * outputScale);
    canvas.height = Math.floor(viewport.height * outputScale);
    const context = canvas.getContext('2d');
    if (!context) continue;
    await page.render({ canvas, canvasContext: context, viewport, transform: outputScale === 1 ? undefined : [outputScale, 0, 0, outputScale, 0, 0] }).promise;
    if (activeDocument !== documentProxy) return;
    void updatePageLinks(activeDocument, number, page, viewport);
  }
}

// PDF.js renders into one canvas at a time. Serialising redraws prevents a
// resize (including the scrollbar appearing) from cancelling the remaining
// mobile pages halfway through their initial render.
function queueRender() {
  renderQueue = renderQueue.catch(() => undefined).then(() => renderPages());
  return renderQueue;
}

async function loadDocument() {
  loading.value = true; loadError.value = false; pageCount.value = 0; currentPage.value = 1; pageCanvases.clear(); pageSizes.value = {}; pageLinks.value = {};
  try {
    const pdfjs = await import('pdfjs-dist');
    const worker = await import('pdfjs-dist/build/pdf.worker.min.mjs?url');
    pdfjs.GlobalWorkerOptions.workerSrc = worker.default;
    await documentProxy?.loadingTask.destroy();
    const bytes = props.source instanceof Blob
      ? new Uint8Array(await props.source.arrayBuffer())
      : new Uint8Array(await (await fetch(props.source)).arrayBuffer());
    documentProxy = await pdfjs.getDocument({ data: bytes }).promise;
    pageCount.value = documentProxy.numPages;
    loading.value = false;
    await nextTick();
    await queueRender();
  } catch { loadError.value = true; }
  finally { loading.value = false; }
}

function previousPage() {
  currentPage.value = Math.max(1, currentPage.value - 1);
}

function nextPage() {
  currentPage.value = Math.min(pageCount.value, currentPage.value + 1);
}

watch(() => props.source, () => void loadDocument());
onMounted(async () => {
  // Rendering a page changes the stack height. Watching every size change here
  // would cancel the in-progress render after page one on narrow screens.
  // The PDF scale only depends on width, so react solely to an actual width
  // change (for example, an orientation change).
  if (stage.value) observedStageWidth = Math.round(stage.value.clientWidth);
  resizeObserver = new ResizeObserver(() => {
    const width = Math.round(stage.value?.clientWidth || 0);
    if (!width || width === observedStageWidth) return;
    observedStageWidth = width;
    if (!loading.value && !loadError.value) void queueRender();
  });
  if (stage.value) resizeObserver.observe(stage.value);
  await loadDocument();
});
onBeforeUnmount(() => { void documentProxy?.loadingTask.destroy(); resizeObserver?.disconnect(); });
</script>

<template>
  <section class="relative min-h-[100svh] overflow-hidden proposal-reader-shell">
    <div ref="stage" :class="['proposal-reader-stage min-h-[100svh]', { 'has-backdrop-image': Boolean(backdropImageUrl) }]" :style="backdropImageUrl ? { '--proposal-reader-image': `url(${backdropImageUrl})` } : undefined">
      <p v-if="loading" class="flex min-h-[100svh] items-center justify-center text-sm text-white/80">{{ loadingLabel }}</p>
      <div v-else-if="loadError" class="flex min-h-[100svh] flex-col items-center justify-center gap-3 px-6 text-center text-sm text-white/80"><p>{{ errorLabel }}</p><Button variant="secondary" @click="emit('open')"><Download class="mr-1.5 size-4" />Open PDF</Button></div>
      <div v-else class="proposal-page-stack">
        <div v-for="page in pageCount" :key="page" :class="['proposal-page-frame', { 'is-current': page === currentPage }]">
          <div class="proposal-page-content" :style="pageSizes[page] ? { '--proposal-page-width': `${pageSizes[page].width}px`, aspectRatio: `${pageSizes[page].width} / ${pageSizes[page].height}` } : undefined">
            <canvas :ref="element => setPageCanvas(page, element)" :aria-label="`${title}, page ${page}`" class="block max-w-full bg-white" />
            <div class="proposal-page-links">
              <a
                v-for="(link, index) in pageLinks[page] || []"
                :key="`${link.href}-${index}`"
                :href="link.href"
                target="_blank"
                rel="noopener noreferrer"
                :aria-label="`Open link: ${link.href}`"
                :title="link.href"
                :style="{ left: `${link.left}%`, top: `${link.top}%`, width: `${link.width}%`, height: `${link.height}%` }"
              ><span class="sr-only">Open link in a new tab</span></a>
            </div>
          </div>
        </div>
      </div>
      <nav v-if="!loading && !loadError && pageCount > 1" class="proposal-page-controls" aria-label="Document pages">
        <Button variant="secondary" size="icon" :disabled="currentPage === 1" aria-label="Previous page" @click="previousPage"><ChevronLeft class="size-5" /></Button>
        <span>{{ currentPage }} / {{ pageCount }}</span>
        <Button variant="secondary" size="icon" :disabled="currentPage === pageCount" aria-label="Next page" @click="nextPage"><ChevronRight class="size-5" /></Button>
      </nav>
    </div>
    <slot name="actions" />
  </section>
</template>

<style scoped>
.proposal-reader-shell { background: var(--brand-dark-background, #1b1b1b); }
.proposal-reader-stage { background-color: var(--brand-dark-background, #1b1b1b); background-image: var(--proposal-reader-image, none); background-position: center; background-size: cover; }
.proposal-reader-stage::before { position: fixed; inset: 0; z-index: 0; background: color-mix(in srgb, var(--brand-dark-background, #1b1b1b) 85%, transparent); content: ''; opacity: 0; pointer-events: none; }
.proposal-reader-stage.has-backdrop-image::before { opacity: 1; }
.proposal-page-stack { position: relative; z-index: 1; min-height: 100svh; }
.proposal-page-frame { display: none; min-width: 0; overflow: hidden; background: color-mix(in srgb, var(--brand-dark-background, #1b1b1b) 82%, transparent); }
.proposal-page-frame.is-current { display: flex; align-items: center; justify-content: center; min-height: 100svh; }
.proposal-page-content { position: relative; width: var(--proposal-page-width); max-width: 100%; overflow: hidden; background: white; }
.proposal-page-content canvas { width: 100%; height: 100%; }
.proposal-page-links { position: absolute; inset: 0; z-index: 1; pointer-events: none; }
.proposal-page-links a { position: absolute; display: block; pointer-events: auto; background: transparent; }
.proposal-page-links a:focus-visible { outline: 2px solid #2563eb; outline-offset: 1px; }
.proposal-page-controls { position: fixed; z-index: 2; right: 1.5rem; bottom: 1.5rem; display: flex; align-items: center; gap: .5rem; padding: .375rem; border: 1px solid color-mix(in srgb, var(--brand-dark-background, #1b1b1b) 22%, transparent); border-radius: .75rem; background: color-mix(in srgb, white 92%, transparent); color: var(--brand-dark-background, #1b1b1b); box-shadow: 0 12px 30px rgb(0 0 0 / .16); }
.proposal-page-controls span { min-width: 3.5rem; text-align: center; font-size: .75rem; font-weight: 600; }
@media (max-width: 767px) {
  .proposal-page-stack { display: grid; min-height: auto; gap: 2px; }
  .proposal-page-frame, .proposal-page-frame.is-current { display: flex; align-items: stretch; justify-content: stretch; min-height: 0; }
  .proposal-page-content { width: 100%; }
  .proposal-page-frame canvas { width: 100% !important; height: auto !important; }
  .proposal-page-controls { display: none; }
}
</style>
